// Almacén local cifrado (IndexedDB) para el modo sin conexión de ClinPed.
// - "cache": respuestas GET (solo lectura offline), cifradas en reposo.
// - "outbox": cola de cambios hechos sin conexión (crear/editar pacientes, consultas, etc.).
// - "idmap": mapeo de IDs temporales (local_*) -> IDs reales del servidor tras sincronizar.
// - "meta": clave de cifrado (AES-GCM, no exportable) y metadatos.
import { openDB } from "idb";

const DB_NAME = "clinped_offline";
const VERSION = 1;

let dbPromise;
function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("cache")) db.createObjectStore("cache");
        if (!db.objectStoreNames.contains("outbox")) db.createObjectStore("outbox", { keyPath: "id", autoIncrement: true });
        if (!db.objectStoreNames.contains("idmap")) db.createObjectStore("idmap");
        if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta");
      },
    });
  }
  return dbPromise;
}

// ---------- Cifrado (AES-GCM, clave no exportable persistida) ----------
let keyPromise;
async function getKey() {
  if (!keyPromise) {
    keyPromise = (async () => {
      const db = await getDB();
      let key = await db.get("meta", "cryptoKey");
      if (!key) {
        key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
        await db.put("meta", key, "cryptoKey");
      }
      return key;
    })();
  }
  return keyPromise;
}

async function encJSON(value) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const bytes = new TextEncoder().encode(JSON.stringify(value ?? null));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, bytes);
  return { iv, ct };
}
async function decJSON(blob) {
  if (!blob) return undefined;
  const key = await getKey();
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: blob.iv }, key, blob.ct);
  return JSON.parse(new TextDecoder().decode(pt));
}

export async function encBytes(arrayBuffer) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, arrayBuffer);
  return { iv, ct };
}
export async function decBytes(blob) {
  const key = await getKey();
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: blob.iv }, key, blob.ct);
}

// ---------- Eventos (para que la UI reaccione a cambios/sincronización) ----------
export function emitChange() {
  window.dispatchEvent(new CustomEvent("clinped-offline-change"));
}

// ---------- Caché de lecturas ----------
export async function putCache(key, data) {
  const db = await getDB();
  await db.put("cache", await encJSON(data), key);
}
export async function getCache(key) {
  const db = await getDB();
  return decJSON(await db.get("cache", key));
}

// ---------- Outbox (cola de cambios) ----------
// item: { method, url, kind:'json'|'multipart', body(obj), fields(obj), file:{iv,ct,name,type}|null,
//         entity, tempId, refPatientId, title, created_at }
export async function enqueue(item) {
  const db = await getDB();
  const record = {
    method: item.method,
    url: item.url,
    kind: item.kind || "json",
    body: item.body != null ? await encJSON(item.body) : null,
    fields: item.fields != null ? await encJSON(item.fields) : null,
    file: item.file || null,
    entity: item.entity || "",
    tempId: item.tempId || null,
    refPatientId: item.refPatientId || null,
    title: item.title || "",
    created_at: item.created_at || new Date().toISOString(),
    tries: 0,
    error: null,
  };
  const id = await db.add("outbox", record);
  emitChange();
  return id;
}

export async function listOutbox() {
  const db = await getDB();
  const raw = await db.getAll("outbox");
  raw.sort((a, b) => a.id - b.id);
  return Promise.all(
    raw.map(async (r) => ({
      ...r,
      body: r.body ? await decJSON(r.body) : null,
      fields: r.fields ? await decJSON(r.fields) : null,
    }))
  );
}

export async function deleteOutbox(id) {
  const db = await getDB();
  await db.delete("outbox", id);
  emitChange();
}

export async function patchOutbox(id, patch) {
  const db = await getDB();
  const rec = await db.get("outbox", id);
  if (!rec) return;
  await db.put("outbox", { ...rec, ...patch });
  emitChange();
}

export async function outboxCount() {
  const db = await getDB();
  return db.count("outbox");
}

// ---------- Mapeo de IDs temporales -> reales ----------
export async function setRealId(tempId, realId) {
  const db = await getDB();
  await db.put("idmap", realId, tempId);
}
export async function getRealId(tempId) {
  const db = await getDB();
  return db.get("idmap", tempId);
}

// ---------- Metadatos ----------
export async function setMeta(key, value) {
  const db = await getDB();
  await db.put("meta", value, key);
}
export async function getMeta(key) {
  const db = await getDB();
  return db.get("meta", key);
}

export async function clearAll() {
  const db = await getDB();
  await Promise.all([db.clear("cache"), db.clear("outbox"), db.clear("idmap")]);
  emitChange();
}
